import { cn } from "../../lib/utils";

/**
 * The 1–5 scale, as a literal union rather than `number`.
 *
 * A 0 or a 6 reaching a verdict makes every average wrong with no error anywhere,
 * so the impossible values are unrepresentable here too — not only at the API
 * boundary. The scale itself is not a parameter: it is the one the medical team
 * validated, anchored "pas du tout satisfaisant" to "tout à fait satisfaisant".
 */
export type RatingValue = 1 | 2 | 3 | 4 | 5;

const VALUES = [1, 2, 3, 4, 5] as const satisfies readonly RatingValue[];

/**
 * One rated criterion: five numbered targets, with the scale's anchors under
 * them.
 *
 * `role="radiogroup"` with real `role="radio"` children rather than five loose
 * buttons, so a screen reader announces "3 of 5" instead of five unrelated
 * controls — this is filled by clinicians on a phone, often one-handed.
 *
 * `value` starts `null`: "not yet answered" and "answered 1" are different
 * states, and collapsing them would silently score every untouched criterion.
 */
export function RatingRow({
  value,
  onChange,
  label,
  description,
  minLabel,
  maxLabel,
  className,
}: {
  value: RatingValue | null;
  onChange: (value: RatingValue) => void;
  label: string;
  // The parenthetical the validated questionnaire carries under each criterion.
  description: string;
  // The scale's anchors. Required-nullable rather than optional so a caller has
  // to decide whether this row shows them (only the first row in a stack does —
  // repeating them five times is noise).
  minLabel: string | null;
  maxLabel: string | null;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-col gap-0.5 font-dm-sans">
        <span className="text-ui-body font-semibold text-ds-moss-800">
          {label}
        </span>
        <span className="text-ui-note font-normal text-ds-slate-600">
          {description}
        </span>
      </div>
      <div
        role="radiogroup"
        aria-label={label}
        className="flex items-center gap-2"
      >
        {VALUES.map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={String(option)}
              onClick={() => {
                onChange(option);
              }}
              className={cn(
                // 44px — the minimum comfortable touch target, and the reason
                // this is not a compact segmented control.
                "size-11 shrink-0 rounded-ds-button-md",
                "font-dm-sans text-ui-body font-medium",
                "cursor-pointer transition-colors",
                "focus-visible:outline-none focus-visible:shadow-focus-ring",
                selected
                  ? "bg-ds-moss-900 text-ds-white"
                  : "border border-ds-slate-200 bg-white text-ds-moss-800 hover:bg-ds-slate-100"
              )}
            >
              {option}
            </button>
          );
        })}
      </div>
      {minLabel === null || maxLabel === null ? null : (
        <div className="flex justify-between font-dm-sans text-ui-note font-normal text-ds-slate-600">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
}
